import { useCallback, useMemo, useState } from "react";
import { noop } from "lodash";
import { useApolloClient } from "@apollo/client";

import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { filterByConnection } from "../filterByConnection";
import { BasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { gqlActions } from "../gqlData_Reducer";

import { GET_SYSTEM_INFO, SystemsResponse } from "./GQLCalls/getSystemCall";
import { SystemItem } from "./oldSystemSlice";
import { MAINTAIN_SYSTEM_IMAGE, MAINTAIN_SYSTEM_INFO, MaintainSystemImageResponse, MaintainSystemInput, MaintainSystemResponse } from "./GQLCalls/maintainSystemCall";
import { EMERGENCY_STOP_SYSTEM, EmergencyStopSystemResponse } from "./GQLCalls/emergencyStopSystemCall";
import { clearSystemOptions } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { CREATE_SYSTEM, CreateSystemResponse } from "./GQLCalls/createSystem";
import { GET_SYSTEM_LIMITATION_INFO, MAINTAIN_SYSTEM_LIMITATIONS, maintainSystemLimitationsResponse, SystemLimitationsResponse } from "./GQLCalls/systemLimitations";
import { upsertSystemLimitations } from "./systemLimitationsSlice";

interface maintainSystemElements {
  emergencyStopSystem: (systemId: number, specifik?: Graph_QL.GQLCons) => Promise<boolean>;
  maintainSystemImage: (systemId: number, image?: string) => Promise<void>;
  createSystem: (domain: string, type: number, name: string) => Promise<{ success: boolean, system_id: number }>;
  getSystemLimitationsInfo: (domain: string) => Promise<void>;
  maintainSystemLimitations: (domain: string, systemTypeId: number, systemsLimit?: number, stationsLimit?: number) => Promise<void>;
}

export const useSystem = (connection: Graph_QL.GQLCons): GqlHook_Returns<SystemItem, BasicQueryInfoInput> & maintainSystemElements => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const [, setCursorId] = useState(-1);

  const systemsState = useAppSelector(({ graphQlData: { domain } }) => domain.systems);
  const { fullState } = useMemo(() => {
    const state = filterByConnection(systemsState, Graph_QL.ToArr(connection)) as Record<string, SystemItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = { ...data.data, domain: key };
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    }, { fullState: state, data: [] } as StateDataObject<SystemItem>)
  }, [connection, systemsState]);

  const getSystemsInfo = useCallback(async (input: BasicQueryInfoInput, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific?.connection ? specific.connection : connection.connection;
    try {
      client.query<SystemsResponse>({ query: GET_SYSTEM_INFO, variables: { input, ...usedConnection }, fetchPolicy: "no-cache" })
        .then((e) => {
          if (e && e.data) {
            dispatch({ type: String(gqlActions.domain.systems.maintained), payload: { con: connection?.connection, data: e.data.system.info } });
            setCursorId(e.data.system.info.at(-1)?.system_id ?? -1)
          }
        }).catch((error) => { console.log(error); })
    } catch (error) {
      console.log(error);
    }
  }, [client, connection.connection, dispatch])

  const getSystemLimitationsInfo = useCallback(async (domain: string) => {
    try {
      client.query<SystemLimitationsResponse>({ query: GET_SYSTEM_LIMITATION_INFO, variables: { domain }, fetchPolicy: "no-cache" })
        .then((e) => {
          if (e && e.data) {
            dispatch(upsertSystemLimitations(e.data.system.systemLimitations));
          }
        }).catch((error) => { console.log(error); })
    } catch (error) {
      console.log(error);
    }
  }, [client, dispatch])

  const maintainSystemLimitations = async (domain: string, systemTypeId: number, systemsLimit?: number, stationsLimit?: number) => {
    client.mutate<maintainSystemLimitationsResponse>({ mutation: MAINTAIN_SYSTEM_LIMITATIONS, variables: { domain, systemType: systemTypeId, systemsLimit, stationsLimit }, context: { headers: { tocloud: true } } })
      .then((response) => {
        if (response && response.data) {
          dispatch(upsertSystemLimitations(response.data.company.maintainCompanyLimitation.updatedValues));
        }
      }).catch((error) => { console.log(error); })
  }

  const maintainEntries = async (systemData?: Partial<DynamicDataObk<MaintainSystemInput>>) => {
    const system = systemData?.data;

    try {
      if (system) {
        client.mutate<MaintainSystemResponse>({ mutation: MAINTAIN_SYSTEM_INFO, variables: { ...connection.connection, system } })
          .then((response) => {
            if (response && response.data) {
              dispatch(clearSystemOptions());
              dispatch({ type: String(gqlActions.domain.systems.maintained), payload: { con: connection?.connection, data: response.data.system.maintain.updatedValues } });
            }
          })
      }
    } catch (error) {
      console.log(error);
    }
  }

  const createSystem = useCallback(async (domain: string, type: number, name: string) => {
    try {
      const response = await client.mutate<CreateSystemResponse>({ mutation: CREATE_SYSTEM, variables: { domain, type, name } })
      if (response) {
        const createdSystem = response.data?.system.create;
        if (createdSystem && createdSystem.success && createdSystem.updatedValues) {
          const createdSystemInfo = createdSystem.updatedValues[0];
          dispatch({ type: String(gqlActions.domain.systems.maintained), payload: { con: connection?.connection, data: createdSystem.updatedValues } });
          return { success: true, system_id: createdSystemInfo.system_id }
        }
      }
      console.log("createSystem response:", response.data?.system.create);
      return { success: false, system_id: -1 };
    } catch (error) {
      return { success: false, system_id: -1 };
    }
  }, [client, connection?.connection, dispatch])

  const emergencyStopSystem = useCallback(async (systemId: number, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific ? specific.connection : connection.connection;
    try {
      return client.mutate<EmergencyStopSystemResponse>({ mutation: EMERGENCY_STOP_SYSTEM, variables: { ...usedConnection, systemId } })
        .then((e) => {
          const doneEmergencyStop = e.data?.system.emergencyStop;
          if (doneEmergencyStop) {
            return doneEmergencyStop.success ? true : false;
          }
          return false;
        }).catch((error) => { return false })
    } catch (error) {
      return false
    }
  }, [client, connection.connection])

  const maintainSystemImage = async (systemId: number, image?: string) => {
    try {
      const response = await client.mutate<MaintainSystemImageResponse>({ mutation: MAINTAIN_SYSTEM_IMAGE, variables: { ...connection.connection, systemId, image } })
      if (response && response.data) {
        dispatch({ type: String(gqlActions.domain.systems.maintained), payload: { con: connection?.connection, data: response.data.system.image.updatedValues } });
      }
    } catch (error) {
      console.log(error);
    }
  }



  return { call: getSystemsInfo, emergencyStopSystem, fetchMore: noop, deleteEntries: noop, data: fullState, maintainEntries, maintainSystemImage, getSystemLimitationsInfo, maintainSystemLimitations, createSystem }
} 

